import { useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '../components/PageHeader'
import {
  Button,
  Chip,
  EmptyState,
  ProviderTag,
  Skeleton,
  StatusChip,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../components/ui'
import { useProviderCapabilities, useSyncAllSystems, useSyncRuns, useSystems } from '../api/hooks'
import { api, ApiError } from '../api/client'
import type { AccessSystem, SystemProvider } from '../api/types'
import { useAuthStore } from '../stores/auth'
import { relativeTime } from '../lib/format'
import { MappingBadge } from '../components/features/MappingCallout'

const providerNames: Record<SystemProvider, string> = {
  axema: 'Axema Vaka',
  techfass: 'TECHFASS',
  acre: 'Acre / SPC',
  came: 'CAME BPT',
  wisenet: 'Wisenet WAVE',
}

/** Managed-site labels from SystemOut.managed_sites; null when none. */
function siteLabels(system: AccessSystem): string | null {
  return system.managed_sites.length > 0 ? system.managed_sites.join(', ') : null
}

function errDetail(e: unknown): string {
  if (e instanceof ApiError) return e.detail
  if (e instanceof Error) return e.message
  return 'Request failed'
}

export default function SystemsPage() {
  const navigate = useNavigate()
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const isAdmin = useAuthStore((s) => s.customer?.role === 'admin')
  const queryClient = useQueryClient()

  const systems = useSystems()
  const syncRuns = useSyncRuns()

  /** Latest sync timestamp per system uid, for the LAST SYNC column. */
  const lastSync = useMemo(() => {
    const map = new Map<string, string>()
    for (const run of syncRuns.data?.items ?? []) {
      const at = run.finished_at ?? run.started_at
      if (!at) continue
      const prev = map.get(run.system)
      if (!prev || at > prev) map.set(run.system, at)
    }
    return map
  }, [syncRuns.data])

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [scope] })
  const verify = useMutation({ mutationFn: api.verifySystem, onSuccess: invalidate })
  // The provider matrix drives the sync affordance — no provider checks.
  const providerCaps = useProviderCapabilities()
  const sync = useMutation({
    mutationFn: (system: AccessSystem) => api.syncSystemNow(system.unique_id),
    onSuccess: invalidate,
  })
  const syncAll = useSyncAllSystems()

  const providerCounts = useMemo(() => {
    const counts = new Map<SystemProvider, number>()
    for (const s of systems.data ?? []) {
      counts.set(s.provider, (counts.get(s.provider) ?? 0) + 1)
    }
    return counts
  }, [systems.data])
  const actionError = verify.error ?? sync.error ?? syncAll.error

  function rowAction(s: AccessSystem) {
    switch (s.status) {
      case 'active':
        return (providerCaps.data?.[s.provider]?.sync_kinds.length ?? 0) === 0 ? null : (
          <Button
            variant="ghost"
            size="sm"
            disabled={sync.isPending}
            onClick={() => sync.mutate(s)}
          >
            Pull now
          </Button>
        )
      case 'needs_sites':
        return (
          <Button variant="ghost" size="sm" onClick={() => navigate(`/systems/${s.unique_id}`)}>
            Select sites
          </Button>
        )
      case 'auth_failed':
        return (
          <Button variant="danger" size="sm" onClick={() => navigate(`/systems/${s.unique_id}`)}>
            Reauthenticate
          </Button>
        )
      case 'draft':
        return (
          <Button
            variant="ghost"
            size="sm"
            disabled={verify.isPending}
            onClick={() => verify.mutate(s.unique_id)}
          >
            Verify
          </Button>
        )
      default:
        return null
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        section="Connections"
        title="Systems"
        action={
          <div className="flex items-center gap-2">
            {isAdmin &&
              Array.from(providerCounts.entries()).map(([provider, count]) => (
                <Button
                  key={provider}
                  variant="ghost"
                  size="sm"
                  disabled={syncAll.isPending}
                  onClick={() => {
                    if (!window.confirm(`Enqueue a full sync for all ${count} ${providerNames[provider] ?? provider} systems?`)) return
                    syncAll.mutate(provider)
                  }}
                >
                  Sync all {providerNames[provider] ?? provider}
                </Button>
              ))}
            <Button onClick={() => navigate('/systems/new')}>+ Add system</Button>
          </div>
        }
      />

      {actionError && (
        <p className="rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger">
          {errDetail(actionError)}
        </p>
      )}

      {systems.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
        </div>
      ) : systems.isError ? (
        <EmptyState title="Could not load systems" hint={systems.error.message} />
      ) : systems.data.length === 0 ? (
        <EmptyState
          title="No systems connected"
          hint="Connect an access-control system to start syncing doors, people and credentials."
          action={<Button onClick={() => navigate('/systems/new')}>+ Add system</Button>}
        />
      ) : (
        <Table>
          <THead>
            <tr>
              <TH>Provider</TH>
              <TH>Installation</TH>
              <TH>Status</TH>
              <TH>Sites</TH>
              <TH>Doors</TH>
              <TH>Last sync</TH>
              <TH className="text-right">Action</TH>
            </tr>
          </THead>
          <TBody>
            {systems.data.map((s) => (
              <TR
                key={s.unique_id}
                className="cursor-pointer"
                onClick={() => navigate(`/systems/${s.unique_id}`)}
              >
                <TD>
                  <span className="flex items-center gap-2">
                    <ProviderTag provider={s.provider} />
                    <span className="text-sm text-ostrel-ink">
                      {providerNames[s.provider] ?? s.provider}
                    </span>
                  </span>
                </TD>
                <TD>
                  <Link
                    to={`/systems/${s.unique_id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-sm font-medium text-ostrel-ink hover:text-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
                  >
                    {s.name}
                  </Link>
                  <MappingBadge systemUid={s.unique_id} />
                </TD>
                <TD>
                  <span className="flex items-center gap-1.5">
                    <StatusChip status={s.status} />
                    {(s.consecutive_sync_failures ?? 0) >= 3 && <Chip tone="danger" label="Offline" />}
                  </span>
                </TD>
                <TD className="text-xs text-ostrel-ink-muted">{siteLabels(s) ?? '—'}</TD>
                <TD className="tnum text-xs text-ostrel-ink-muted">—</TD>
                <TD className="tnum text-xs text-ostrel-ink-muted">
                  {lastSync.has(s.unique_id)
                    ? relativeTime(lastSync.get(s.unique_id) as string)
                    : 'never'}
                </TD>
                <TD className="text-right">
                  <span onClick={(e) => e.stopPropagation()}>{rowAction(s)}</span>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}

      <p className="max-w-3xl text-xs leading-relaxed text-ostrel-ink-muted">
        Connector lifecycle: <strong className="text-ostrel-ink">draft</strong> (credentials saved,
        unverified) → <strong className="text-ostrel-ink">needs sites</strong> (verified,
        installations not chosen) → <strong className="text-ostrel-ink">active</strong>.{' '}
        <strong className="text-ostrel-ink">Auth-failed</strong> connectors stop syncing but retain
        last known door and credential state.
      </p>
    </div>
  )
}
